import {
  getMoonUtcTime,
  getTimezoneBasedNightTime
} from 'framework/base/utils/Utils';
import { sameCurrencyAmountConsistencyTest } from 'framework/desktop/triplex-component/amountConsistency';
import { areTriplexValuesUnchanged } from 'framework/desktop/triplex-component/utils';
import * as Yup from 'yup';

// regexes
const NUMBER_AND_ENGLISH_LETTER_REGEX = /^[0-9a-zA-Z]*$/;
const NUMBER_REGEX = /^[0-9]*$/;
const CAPITAL_ENGLISH_LETTER_REGEX = /^[A-Z]*$/;
const WEBSITE_REGEX =
  /((https?):\/\/)?(www.)?[a-zA-Z0-9]+(\.[a-zA-Z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/;

// messages
const REQUIRED_MESSAGE = 'Required field message';
const FILE_REQUIRED_MESSAGE = 'File required';
const DATE_TYPE_MESSAGE = 'Invalid date';
const NO_FUTURE_DATES_MESSAGE = 'no future dates message';
const EMAIL_TYPE_MESSAGE = 'Invalid email message';
const WEBSITE_TYPE_MESSAGE = 'Invalid website message';
const NUMBER_MESSAGE = 'Number message';
const POSITIVE_MESSAGE = 'Value must be greater than zero';
const EXISTED_USERNAME_MESSAGE = 'Existed username message';
const NUMBER_ENGLISH_LETTERS_MESSAGE = 'Numbers and English letters message';
const CAPITAL_ENGLISH_LETTERS_MESSAGE = 'Capital English letters message';
const UNCHECK_MESSAGE = 'Uncheck message';
const COMMISSION_LOWER_THAT_AMOUNT_MESSAGE =
  'The commission amount must be less than the transaction amount';
const DUE_DATE_BIGGER_THAN_DATE_MESSAGE = 'Cheque date message';
const IBAN_MESSAGE = 'Iban message';
const CARD_NUMBER_MESSAGE = 'Card number message';
const OTHER_OCCUPATION_ON_OTHER_OCCUPATION_CODE_REQUIRED_MESSAGE =
  'Other occupation message';
const CUSTOMER_AGE_LIMIT_MESSAGE = 'Customer age limit message';
const ISSUE_DATE_CAN_NOT_BE_GREATER_THAN_TODAY_MESSAGE = 'Issue date message';
const EXPIRY_DATE_MUST_BE_GREATER_THAN_ISSUE_DATE_MESSAGE =
  'Expire date message';
const PHONE_NUMBER_MINIMUM_CHARACTER_MESSAGE =
  'Phone number minimum length message';
const PHONE_NUMBER_MAXIMUM_15_CHARACTER_MESSAGE =
  'Phone number maximum length message';
const PHONE_NUMBER_NOT_START_WITH_ZERO = 'Phone number start message';
const MAX_NUMBER_IS_100_MESSAGE = 'max number message';
const INVALID_TRADE_RATE_RANGE_MESSAGE = 'Invalid trade rate';
const NOT_MORE_AMOUNT_PAYMENT_ORDER =
  'The Entered Amount Is Bigger Than The Sum Of The Payment Order';
const REQUIRED_CURRNECY_MESSAGE = 'Required currency';
const NATIONAL_ID_MINIMUM_LENGTH =
  'National ID length cannot be less than 6 characters';
const NATIONAL_ID_MAXIMUM_LENGTH =
  'National ID length cannot exceed 15 characters';
const POSTAL_CODE_MINIMUM_LENGTH =
  'Postal code length cannot be less than 4 characters';
const POSTAL_CODE_MAXIMUM_LENGTH =
  'Postal code length cannot exceed 10 characters';
const REGISTRATION_CODE_MINIMUM_LENGTH =
  'Registration code length cannot be less than 5 characters';
const REGISTRATION_CODE_MAXIMUM_LENGTH =
  'Registration code length cannot exceed 15 characters';
const MAXIMUM_CHARACTER_50_MESSAGE = 'validation.maxLengthExceeded';

// const NUMBER_MUST_BE_GREATER_THAN_OTHER_NUMBER_MESSAGE = 'مقدار این فیلد باید بزرگتر از فیلد قبل باشد ';

// yup objects
const DATE_REQUIRED_YUP_OBJECT = (t) =>
  Yup.date().typeError(t(DATE_TYPE_MESSAGE)).required(t(REQUIRED_MESSAGE));
const PAST_DATE_REQUIRED_YUP_OBJECT = (t) =>
  Yup.date()
    .typeError(t(DATE_TYPE_MESSAGE))
    .max(
      new Date(+getTimezoneBasedNightTime(new Date())),
      t(NO_FUTURE_DATES_MESSAGE)
    )
    .required(t(REQUIRED_MESSAGE));
const PAST_EXACT_DATE_REQUIRED_YUP_OBJECT = (t) =>
  Yup.date()
    .typeError(t(DATE_TYPE_MESSAGE))
    .max(new Date(), t(NO_FUTURE_DATES_MESSAGE))
    .required(t(REQUIRED_MESSAGE));
const PAST_DATE_YUP_OBJECT = (t) =>
  Yup.date()
    .typeError(t(DATE_TYPE_MESSAGE))
    .max(new Date(), t(NO_FUTURE_DATES_MESSAGE))
    .nullable();
const DATE_YUP_OBJECT = (t) =>
  Yup.date().typeError(t(DATE_TYPE_MESSAGE)).nullable();
const EMAIL_YUP_OBJECT = (t) => Yup.string().email(t(EMAIL_TYPE_MESSAGE));
const WEBSITE_YUP_OBJECT = (t) =>
  Yup.string().matches(WEBSITE_REGEX, { message: t(WEBSITE_TYPE_MESSAGE) });
const POSITIVE_AMOUNT_YUP_OBJECT = (t) =>
  Yup.number().typeError(t(NUMBER_MESSAGE)).positive(t(POSITIVE_MESSAGE));
const POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT = (t) =>
  POSITIVE_AMOUNT_YUP_OBJECT(t).required(t(REQUIRED_MESSAGE));
const STRING_REQUIRED_YUP_OBJECT = (t, message = t(REQUIRED_MESSAGE)) =>
  Yup.string().required(t(message));
const NUMBER_REQUIRED_YUP_OBJECT = (t) =>
  Yup.number().required(t(REQUIRED_MESSAGE));
const ONLY_NUMBER_AND_ENGLISH_LETTERS_YUP_OBJECT = (t) =>
  Yup.string().matches(NUMBER_AND_ENGLISH_LETTER_REGEX, {
    message: t(NUMBER_ENGLISH_LETTERS_MESSAGE)
  });
const ONLY_NUMBER_AND_ENGLISH_LETTERS_REQUIRED_YUP_OBJECT = (t) =>
  Yup.string()
    .matches(NUMBER_AND_ENGLISH_LETTER_REGEX, {
      message: t(NUMBER_ENGLISH_LETTERS_MESSAGE)
    })
    .required(t(REQUIRED_MESSAGE));
const ONLY_CAPITAL_ENGLISH_LETTERS_YUP_OBJECT = (t) =>
  Yup.string().matches(CAPITAL_ENGLISH_LETTER_REGEX, {
    message: t(CAPITAL_ENGLISH_LETTERS_MESSAGE)
  });
const IBAN_YUP_OBJECT = (t) =>
  Yup.string().matches(/^[0-9a-zA-Z]{26}$/, {
    message: t(IBAN_MESSAGE)
  });
const CARD_NUMBER_YUP_OBJECT = (t) =>
  Yup.string().matches(/^[0-9a-zA-Z]{16}$/, {
    message: t(CARD_NUMBER_MESSAGE)
  });
const REQUIRED_CURRNECY = (t) =>
  Yup.string().required(t(REQUIRED_CURRNECY_MESSAGE));
const RELATIVE_STRING_YUP_OBJECT = (
  relativeField,
  is,
  thenYupObject,
  otherwiseYupObject
) =>
  Yup.string().when(relativeField, {
    is,
    then: thenYupObject,
    otherwise: otherwiseYupObject
  });
const RELATIVE_NUMBER_YUP_OBJECT = (
  relativeField,
  is,
  thenYupObject,
  otherwiseYupObject
) =>
  Yup.number().when(relativeField, {
    is,
    then: thenYupObject,
    otherwise: otherwiseYupObject
  });
const RELATIVE_DATE_YUP_OBJECT = (
  relativeField,
  is,
  thenYupObject,
  otherwiseYupObject
) =>
  Yup.date().when(relativeField, {
    is,
    then: thenYupObject,
    otherwise: otherwiseYupObject
  });
const FILE_REQUIRED_YUP_OBJECT = (t) =>
  Yup.mixed().test('required', t(FILE_REQUIRED_MESSAGE), (file) => {
    if (file) return true;
    return false;
  });
const PHONE_NUMBER_REQUIRED_YUP_OBJECT = (t) =>
  Yup.string()
    .matches(NUMBER_REGEX, { message: t(NUMBER_MESSAGE) })
    .required(t(REQUIRED_MESSAGE))
    .test(
      'starts-with-zero',
      t(PHONE_NUMBER_NOT_START_WITH_ZERO),
      // eslint-disable-next-line func-names
      function (val) {
        if (!val) return true;

        const countryCode = this.parent?.countryCode;

        if (countryCode === '992') return true;

        return !val.startsWith('0');
      }
    )
    // eslint-disable-next-line func-names
    .test('len', t(PHONE_NUMBER_MINIMUM_CHARACTER_MESSAGE), function (val) {
      const countryCode = this.parent?.countryCode;
      const len = val?.length;

      if (!val) return true;

      if (countryCode === '45') return len === 8;

      return len >= 9;
    })
    .test(
      'len',
      t(PHONE_NUMBER_MAXIMUM_15_CHARACTER_MESSAGE),
      (val) => val?.toString().length <= 15
    );
const PHONE_NUMBER_YUP_OBJECT = (t) =>
  Yup.string()
    .matches(NUMBER_REGEX, { message: t(NUMBER_MESSAGE) })
    .test(
      'starts-with-zero',
      t(PHONE_NUMBER_NOT_START_WITH_ZERO),
      // eslint-disable-next-line func-names
      function (val) {
        if (!val) return true;

        const countryCode = this.parent?.countryCode;

        if (countryCode === '992') return true;

        return !val.startsWith('0');
      }
    )
    // eslint-disable-next-line func-names
    .test('len', t(PHONE_NUMBER_MINIMUM_CHARACTER_MESSAGE), function (val) {
      const countryCode = this.parent?.countryCode;
      const len = val?.length;

      if (!val) return true;

      if (countryCode === '45') return len === 8;

      return len >= 9;
    })
    .test('len', t(PHONE_NUMBER_MAXIMUM_15_CHARACTER_MESSAGE), (val) => {
      const phoneNumberLength = val?.toString().length;
      return phoneNumberLength === undefined || val?.toString().length <= 15;
    });

const CURRENCY_OBJ_YUP_OBJECT = (t) =>
  Yup.object().required(t(REQUIRED_MESSAGE)).nullable();
const PHONE_NUMBER_IRAN = (t) =>
  Yup.string()
    .required(t('Required field message'))
    .matches(/^09\d{9}$/, t('PhoneNumber must be 11 digits and start with 0'));
const PHONE_NUMBER_TURKEY = (t) =>
  Yup.string()
    .required(t('Required field message'))
    .matches(/^05\d{9}$/, t('TurkeyPhoneNumberValidation'));
const PHONE_NUMBER_AFGHAN = (t) =>
  Yup.string()
    .required(t('Required field message'))
    .matches(/^07\d{8}$/, t('AfghanPhoneNumberValidation'));
const PHONE_NUMBER_PAKISTAN = (t) =>
  Yup.string()
    .required(t('Required field message'))
    .matches(/^03\d{9}$/, t('PakistanPhoneNumberValidation'));
const JUST_IRAN_PHONE_NUMBER = (t) =>
  Yup.string()
    .required(t('Required field message'))
    .matches(/^09[0-39]\d{8}$/, t('JustIranPhoneNumber'));
const PUBG_ID_LENGTH = (t) =>
  Yup.string()
    .required(t('Required field message'))
    .min(10, t('minPubgIdLength'))
    .max(12, t('maxPubgIdLength'));

// common logical functions
function checkCommissionLogical({
  values,
  setFieldError,
  hasCommission,
  commissionAmount,
  commissionCurrencyCode,
  amount,
  currencyCode,
  isAlwaysChecked = false,
  t,
  IsNewForm = true
}) {
  if (
    !IsNewForm &&
    !isAlwaysChecked &&
    values[hasCommission] &&
    (!values[commissionAmount] || !values[commissionCurrencyCode])
  ) {
    setFieldError(commissionAmount, t(UNCHECK_MESSAGE));
    return false;
  }
  if (
    IsNewForm &&
    (isAlwaysChecked || values[hasCommission]) &&
    values[commissionCurrencyCode] === values[currencyCode] &&
    values[commissionAmount] >= values[amount]
  ) {
    setFieldError(commissionAmount, t(COMMISSION_LOWER_THAT_AMOUNT_MESSAGE));
    return false;
  }
  return true;
}

function compareTwoDates({
  values,
  setFieldError,
  biggerDate,
  smallerDate,
  message,
  errorOnBiggerDate,
  t
}) {
  if (values[smallerDate].getTime() > values[biggerDate].getTime()) {
    if (errorOnBiggerDate) {
      setFieldError(biggerDate, t(message));
    } else {
      setFieldError(smallerDate, t(message));
    }
    return false;
  }
  return true;
}

function calculateTwoDatesDifferencesInDays(biggerDate, smallerDate) {
  if (!biggerDate || !smallerDate) {
    return null;
  }

  const biggerTime = biggerDate.getTime();
  const smallerTime = smallerDate.getTime();

  return Math.floor((biggerTime - smallerTime) / (24 * 3600 * 1000));
}

function checkTradeRateValidation({
  values,
  setFieldError,
  tradeRate,
  isTradeRateValid,
  t
}) {
  if (!values[isTradeRateValid]) {
    setFieldError(tradeRate, t(INVALID_TRADE_RATE_RANGE_MESSAGE));
    return false;
  }
  return true;
}

// function checkGreaterNumber({
//   numOne,
//   numTwo,
//   setFieldError,
//   numOneFiedld,
//   t
// }) {
//   if (numOne < numTwo) {
//     setFieldError(numOneFiedld, t(NUMBER_MUST_BE_GREATER_THAN_OTHER_NUMBER_MESSAGE));
//     return false;
//   }
//   return true;
// }

/* ***************** validation schemas ********************* */
export const cashTransactionValidationSchema = (t) =>
  Yup.object({
    date: PAST_DATE_REQUIRED_YUP_OBJECT(t),
    cashDeskId: STRING_REQUIRED_YUP_OBJECT(t),
    accountNumber: STRING_REQUIRED_YUP_OBJECT(t),
    amount: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
    currencyCode: STRING_REQUIRED_YUP_OBJECT(t),
    commissionAmount: POSITIVE_AMOUNT_YUP_OBJECT(t),
    isWithdraw: Yup.boolean().required(t(REQUIRED_MESSAGE))
  });

export const interbankTransactionValidationSchema = (t) =>
  Yup.object({
    date: PAST_EXACT_DATE_REQUIRED_YUP_OBJECT(t),
    debtorBankId: STRING_REQUIRED_YUP_OBJECT(t),
    amount: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
    creditorBankId: STRING_REQUIRED_YUP_OBJECT(t),
    transactionNumber: ONLY_NUMBER_AND_ENGLISH_LETTERS_YUP_OBJECT(t)
  });

export const bankTransactionValidationSchema = (t) =>
  Yup.object({
    date: PAST_EXACT_DATE_REQUIRED_YUP_OBJECT(t),
    bankAccountId: STRING_REQUIRED_YUP_OBJECT(t),
    transactionNumber: ONLY_NUMBER_AND_ENGLISH_LETTERS_YUP_OBJECT(t),
    bankCommissionAmount: POSITIVE_AMOUNT_YUP_OBJECT(t)
      .when(['amountsSum'], (amountsSum, schema) =>
        schema.test(
          'is-less-than-sum',
          t(COMMISSION_LOWER_THAT_AMOUNT_MESSAGE),
          (value) => {
            // Corrected function declaration
            if (value == null) return true; // Allow null if needed
            return amountsSum != null && value < amountsSum;
          }
        )
      )
      .nullable(),

    depositInfos: Yup.array().when('isKnown', {
      is: true,
      then: Yup.array().of(
        Yup.object({
          amount: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
          currencyCode: STRING_REQUIRED_YUP_OBJECT(t),
          accountId: STRING_REQUIRED_YUP_OBJECT(t),
          commissionAmount: POSITIVE_AMOUNT_YUP_OBJECT(t)
        })
      ),
      otherwise: Yup.array().of(
        Yup.object({
          amount: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
          currencyCode: STRING_REQUIRED_YUP_OBJECT(t),
          commissionAmount: POSITIVE_AMOUNT_YUP_OBJECT(t)
        })
      )
    })
  });

export const newCreditBankTransactionValidationSchema = (t) =>
  Yup.object({
    date: PAST_EXACT_DATE_REQUIRED_YUP_OBJECT(t),
    bankAccountId: STRING_REQUIRED_YUP_OBJECT(t),
    transactionNumber: ONLY_NUMBER_AND_ENGLISH_LETTERS_REQUIRED_YUP_OBJECT(t)
  });

export const oldCreditBankTransactionValidationSchema = (t) =>
  Yup.object({
    date: DATE_REQUIRED_YUP_OBJECT(t),
    bankAccountId: STRING_REQUIRED_YUP_OBJECT(t),
    bankDocumentId: STRING_REQUIRED_YUP_OBJECT(t)
  });

export const transferBetweenAccountsValidationSchema = (
  t,
  isDebtorAccountNumberOptional = false,
  isCreditorAccountNumberOptional = false
) =>
  Yup.object({
    date: PAST_DATE_REQUIRED_YUP_OBJECT(t),
    debtorAccountNumber: isDebtorAccountNumberOptional
      ? undefined
      : STRING_REQUIRED_YUP_OBJECT(t),
    creditorAccountNumber: isCreditorAccountNumberOptional
      ? undefined
      : STRING_REQUIRED_YUP_OBJECT(t),
    amount: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
    currencyCode: STRING_REQUIRED_YUP_OBJECT(t),
    debtorCommissionAmount: POSITIVE_AMOUNT_YUP_OBJECT(t),
    creditorCommissionAmount: POSITIVE_AMOUNT_YUP_OBJECT(t)
  });

export const submitChequeValidationSchema = (t) =>
  Yup.object({
    date: PAST_DATE_REQUIRED_YUP_OBJECT(t),
    accountNumber: STRING_REQUIRED_YUP_OBJECT(t),
    chequeNumber: NUMBER_REQUIRED_YUP_OBJECT(t),
    amount: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
    currencyCode: STRING_REQUIRED_YUP_OBJECT(t),
    dueDate: DATE_REQUIRED_YUP_OBJECT(t),
    chequeCategory: NUMBER_REQUIRED_YUP_OBJECT(t),
    opponentId: STRING_REQUIRED_YUP_OBJECT(t),
    assignorCommissionAmount: POSITIVE_AMOUNT_YUP_OBJECT(t),
    receiverCommissionAmount: POSITIVE_AMOUNT_YUP_OBJECT(t)
  });

export const tradeCurrencyValidationSchema = (t) =>
  Yup.object().shape(
    {
      date: PAST_DATE_REQUIRED_YUP_OBJECT(t),
      buyAmount: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
      buyCurrencyCode: REQUIRED_CURRNECY(t),
      buyerAccountNumber: STRING_REQUIRED_YUP_OBJECT(t),
      tradeRate: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
      buyCommissionAmount: POSITIVE_AMOUNT_YUP_OBJECT(t).when(
        ['buyCommissionCurrencyCode', 'buyCurrencyCode'],
        {
          is: (buyCommissionCurrencyCode, buyCurrencyCode) =>
            buyCommissionCurrencyCode === buyCurrencyCode,
          then: Yup.number()
            .lessThan(
              Yup.ref('buyAmount'),
              t(COMMISSION_LOWER_THAT_AMOUNT_MESSAGE)
            )
            .nullable()
        }
      ),
      buyerBankReceiptDate: RELATIVE_DATE_YUP_OBJECT(
        ['buyerIsBank', 'transactionId'],
        (buyerIsBank, transactionId) => !transactionId && buyerIsBank,
        PAST_DATE_REQUIRED_YUP_OBJECT(t),
        PAST_DATE_YUP_OBJECT(t)
      ),
      sellAmount: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
      sellCurrencyCode: REQUIRED_CURRNECY(t),
      sellerAccountNumber: STRING_REQUIRED_YUP_OBJECT(t),
      sellCommissionAmount: POSITIVE_AMOUNT_YUP_OBJECT(t).when(
        ['sellCommissionCurrencyCode', 'sellCurrencyCode'],
        {
          is: (sellCommissionCurrencyCode, sellCurrencyCode) =>
            sellCommissionCurrencyCode === sellCurrencyCode,
          then: Yup.number()
            .lessThan(
              Yup.ref('sellAmount'),
              t(COMMISSION_LOWER_THAT_AMOUNT_MESSAGE)
            )
            .nullable()
        }
      ),
      sellerBankReceiptDate: RELATIVE_DATE_YUP_OBJECT(
        ['sellerIsBank', 'transactionId'],
        (sellerIsBank, transactionId) => !transactionId && sellerIsBank,
        PAST_DATE_REQUIRED_YUP_OBJECT(t),
        PAST_DATE_YUP_OBJECT(t)
      ),
      buyerReceiptNumber: RELATIVE_STRING_YUP_OBJECT(
        ['buyerIsBank', 'buyerTransactionId', 'transactionId'],
        (buyerIsBank, buyerTransactionId, transactionId) =>
          !transactionId && buyerIsBank && !buyerTransactionId,
        STRING_REQUIRED_YUP_OBJECT(t),
        undefined
      ),
      buyerTransactionId: RELATIVE_STRING_YUP_OBJECT(
        ['buyerIsBank', 'buyerReceiptNumber', 'transactionId'],
        (buyerIsBank, buyerReceiptNumber, transactionId) =>
          !transactionId && buyerIsBank && !buyerReceiptNumber,
        STRING_REQUIRED_YUP_OBJECT(t),
        undefined
      ),
      sellerReceiptNumber: RELATIVE_STRING_YUP_OBJECT(
        ['sellerIsBank', 'sellerTransactionId', 'transactionId'],
        (sellerIsBank, sellerTransactionId, transactionId) =>
          !transactionId && sellerIsBank && !sellerTransactionId,
        STRING_REQUIRED_YUP_OBJECT(t),
        undefined
      ),
      sellerTransactionId: RELATIVE_STRING_YUP_OBJECT(
        ['sellerIsBank', 'sellerReceiptNumber', 'transactionId'],
        (sellerIsBank, sellerReceiptNumber, transactionId) =>
          !transactionId && sellerIsBank && !sellerReceiptNumber,
        STRING_REQUIRED_YUP_OBJECT(t),
        undefined
      )
    },
    [
      ['buyerReceiptNumber', 'buyerTransactionId'],
      ['sellerReceiptNumber', 'sellerTransactionId']
    ]
  );

export const draftNumberSettingsValidationSchema = (t) =>
  Yup.object({
    incomingDraftNumber: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
    outgoingDraftNumber: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t)
  });

export const addPersonAccountValidationSchema = (t) =>
  Yup.object({
    accountNumber: STRING_REQUIRED_YUP_OBJECT(t)
  });

export const addSystemAccountValidationSchema = (t) =>
  Yup.object({
    accountTitle: STRING_REQUIRED_YUP_OBJECT(t),
    accountType: NUMBER_REQUIRED_YUP_OBJECT(t),
    creditLimits: Yup.array().of(
      Yup.object().shape({
        hasUnlimitedLimit: Yup.boolean(),
        amount: POSITIVE_AMOUNT_YUP_OBJECT(t).when('hasUnlimitedLimit', {
          is: true,
          then: (schema) => schema.nullable().notRequired(),
          otherwise: (schema) => schema
        })
      })
    ),
    openingBalances: Yup.array().of(
      Yup.object().shape({
        amount: POSITIVE_AMOUNT_YUP_OBJECT(t)
      })
    )
  });

export const addCurrencyValidationSchema = (t) =>
  Yup.object({
    currencyCode: STRING_REQUIRED_YUP_OBJECT(t),
    decimalPlaces: NUMBER_REQUIRED_YUP_OBJECT(t),
    dollarRate: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t)
  });

export const addBankAccountValidationSchema = (t) =>
  Yup.object({
    bankName: STRING_REQUIRED_YUP_OBJECT(t),
    currencyCode: STRING_REQUIRED_YUP_OBJECT(t),
    bankAccountNumber: STRING_REQUIRED_YUP_OBJECT(t),
    iBan: IBAN_YUP_OBJECT(t),
    swiftCode: ONLY_CAPITAL_ENGLISH_LETTERS_YUP_OBJECT(t),
    openingBalance: POSITIVE_AMOUNT_YUP_OBJECT(t)
  });

export const updateBankAccountValidationSchema = (t) =>
  Yup.object({
    bankName: STRING_REQUIRED_YUP_OBJECT(t),
    bankAccountNumber: STRING_REQUIRED_YUP_OBJECT(t),
    iBan: IBAN_YUP_OBJECT(t),
    swiftCode: ONLY_CAPITAL_ENGLISH_LETTERS_YUP_OBJECT(t)
  });

export const checkoutByDateValidationSchema = (t) =>
  Yup.object({
    toDraftDate: PAST_DATE_REQUIRED_YUP_OBJECT(t)
  });

export const checkoutByRowValidationSchema = (t) =>
  Yup.object({
    toRow: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t)
  });

export const checkoutByOpeningBalanceValidationSchema = (t) =>
  Yup.object({
    openingBalances: Yup.array().of(
      Yup.object({
        amount: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
        currencyCode: STRING_REQUIRED_YUP_OBJECT(t)
      })
    ),
    toDate: PAST_DATE_REQUIRED_YUP_OBJECT(t)
  });

export const checkoutBySingleOpeningBalanceValidationSchema = (t) =>
  Yup.object({
    amount: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
    currencyCode: STRING_REQUIRED_YUP_OBJECT(t),
    toDate: PAST_DATE_REQUIRED_YUP_OBJECT(t)
  });

export const chequeOperationValidationSchema = (t) =>
  Yup.object({
    date: DATE_REQUIRED_YUP_OBJECT(t),
    chequeCategoryCode: NUMBER_REQUIRED_YUP_OBJECT(t),
    opponentId: STRING_REQUIRED_YUP_OBJECT(t),
    assignorCommissionAmount: POSITIVE_AMOUNT_YUP_OBJECT(t),
    receiverCommissionAmount: POSITIVE_AMOUNT_YUP_OBJECT(t)
  });

export const systemSettingsValidationSchema = (t) =>
  Yup.object({
    exchangeName: STRING_REQUIRED_YUP_OBJECT(t),
    defaultCostName: STRING_REQUIRED_YUP_OBJECT(t),
    defaultCashName: STRING_REQUIRED_YUP_OBJECT(t)
  });

export const systemSettingBankInfosValidationSchema = (t) =>
  Yup.object({
    accountOwnerName: STRING_REQUIRED_YUP_OBJECT(t),
    cardNumber: CARD_NUMBER_YUP_OBJECT(t).required(t(REQUIRED_MESSAGE)),
    iBan: IBAN_YUP_OBJECT(t)
  });

export const personRegistrationBasicInfoValidationSchema = (
  t,
  isUserNameValid,
  hasUserNameChecked
) =>
  Yup.object({
    firstName: STRING_REQUIRED_YUP_OBJECT(t),
    lastName: STRING_REQUIRED_YUP_OBJECT(t),
    contactDetails: Yup.array().of(
      Yup.object({
        phoneNumber: PHONE_NUMBER_YUP_OBJECT(t)
      })
    ),
    userName: Yup.string().test(
      'isUserNameValid',
      ({ value }) =>
        value ? t(EXISTED_USERNAME_MESSAGE) : t(REQUIRED_MESSAGE),
      () => (hasUserNameChecked ? isUserNameValid : true)
    )
  });

export const personRegistrationBasicInfoValidationSchema2 = (
  t,
  isUserNameValid,
  hasUserNameChecked,
  isTelegramEnabled
) =>
  Yup.object().shape({
    firstName: STRING_REQUIRED_YUP_OBJECT(t),
    lastName: STRING_REQUIRED_YUP_OBJECT(t),
    openingBalances: Yup.array().of(
      Yup.object().shape({
        amount: POSITIVE_AMOUNT_YUP_OBJECT(t)
      })
    ),
    contactDetails: Yup.array().of(
      Yup.object({
        phoneNumber: PHONE_NUMBER_YUP_OBJECT(t)
      })
    ),
    whatsappGroupLinks: Yup.array().of(
      Yup.string()
        .nullable()
        .test(
          'validate-communication-link',
          t('validation.invalidLink'),
          (value) => {
            // Allow empty string if it's optional
            if (!value) return true;

            if (isTelegramEnabled) {
              // Validates standard Telegram Chat IDs (e.g. 123456789, -123456789, -1001234567890)
              const telegramChatIdRegex = /^-?\d{5,20}$/;
              return telegramChatIdRegex.test(value.trim());
            }
              const whatsappUrlRegex = /^https:\/\/(chat|wa)\.whatsapp\.com\/[A-Za-z0-9]+/i;
              return whatsappUrlRegex.test(value.trim());
          }
        )
    ),
    userName: Yup.string().test(
      'isUserNameValid',
      ({ value }) => value && t(EXISTED_USERNAME_MESSAGE),
      () => (hasUserNameChecked ? isUserNameValid : true)
    )
  });

export const fastPersonRegistrationValidationSchema = (t) =>
  Yup.object({
    firstName: STRING_REQUIRED_YUP_OBJECT(t),
    lastName: STRING_REQUIRED_YUP_OBJECT(t),
    phoneNumber: PHONE_NUMBER_REQUIRED_YUP_OBJECT(t),
    documentTypeCode: Yup.string().nullable(), // Assuming it's a string
    documentNumber: Yup.string().when('documentTypeCode', {
      is: (value) => !!value, // Check if documentTypeCode has a value
      then: ONLY_NUMBER_AND_ENGLISH_LETTERS_YUP_OBJECT(t).required(
        t(REQUIRED_MESSAGE)
      ),
      otherwise: Yup.string().notRequired()
    })
  });

export const personRegistrationPersonalDetailsValidationSchema = (t) => {
  const OTHER_OCCUPATION_CODE = 3;

  return Yup.object({
    nationalityCode: STRING_REQUIRED_YUP_OBJECT(t),
    birthCountryCode: STRING_REQUIRED_YUP_OBJECT(t),
    birthDate: PAST_DATE_YUP_OBJECT(t),
    email: EMAIL_YUP_OBJECT(t),
    website: WEBSITE_YUP_OBJECT(t),
    otherOccupation: RELATIVE_STRING_YUP_OBJECT(
      'occupationCode',
      OTHER_OCCUPATION_CODE,
      STRING_REQUIRED_YUP_OBJECT(
        t,
        OTHER_OCCUPATION_ON_OTHER_OCCUPATION_CODE_REQUIRED_MESSAGE
      )
    )
  });
};

export const personRegistrationDocumentsStepValidationSchema = (t) =>
  Yup.object({
    documentTypeCode: STRING_REQUIRED_YUP_OBJECT(t),
    issueDate: PAST_DATE_YUP_OBJECT(t),
    expiryDate: DATE_YUP_OBJECT(t),
    documentNumber: ONLY_NUMBER_AND_ENGLISH_LETTERS_YUP_OBJECT(t).required(
      t(REQUIRED_MESSAGE)
    )
  });

export const personRegistrationAddressesStepValidationSchema = (t) =>
  Yup.object({
    countryCode: STRING_REQUIRED_YUP_OBJECT(t)
  });

export const personRegistrationAttachmentStepValidationSchema = (t) =>
  Yup.object({
    file: FILE_REQUIRED_YUP_OBJECT(t)
  });

export const personRegistrationBankInfoStepValidationSchema = (t) =>
  Yup.object({
    originCountryCode: STRING_REQUIRED_YUP_OBJECT(t),
    currencyCode: STRING_REQUIRED_YUP_OBJECT(t),
    bankName: STRING_REQUIRED_YUP_OBJECT(t),
    swiftCode: ONLY_CAPITAL_ENGLISH_LETTERS_YUP_OBJECT(t),
    iBan: IBAN_YUP_OBJECT(t),
    bankAccountNumber: STRING_REQUIRED_YUP_OBJECT(t),
    accountOwnerName: STRING_REQUIRED_YUP_OBJECT(t),
    cardNumber: CARD_NUMBER_YUP_OBJECT(t)
  });

export const increaseCustomerCreditValidationSchema = (t) =>
  Yup.object().shape({
    date: DATE_REQUIRED_YUP_OBJECT(t),
    accountNumber: STRING_REQUIRED_YUP_OBJECT(t),
    creditCurrencyCode: STRING_REQUIRED_YUP_OBJECT(t),
    creditAmount: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
    opponentId: Yup.string().when('isLoan', {
      is: true,
      then: (schema) => schema.notRequired().strip(),
      otherwise: STRING_REQUIRED_YUP_OBJECT(t)
    }),

    paymentType: Yup.string()
      .nullable()
      .required(t('validation.required')),

    bankTransactionNumber: Yup.string()
      .trim()
      .nullable()
      .required(t('validation.required')),

    metadata: Yup.string()
      .nullable()
      .when('paymentType', {
        is: 'CARDTOCARD',
        then: () =>
          CARD_NUMBER_YUP_OBJECT(t).required(t('validation.required')),
        otherwise: (schema) =>
          schema.when('paymentType', {
            is: 'MOBILEBANKINGDEPOSIT',
            then: () =>
              Yup.string()
                .trim()
                .matches(/^\d+$/, t('validation.onlyNumbers'))
                .required(t('validation.required')),
            otherwise: (s) =>
              s.when('paymentType', {
                is: 'ACCOUNTTOACCOUNTRECEIPT',
                then: (sch) => sch.trim().required(t('validation.required')),
                otherwise: (schma) => schma.notRequired()
              })
          })
      })
  });

  // Validation Schema using Yup
export const executePaymentOrderReceiptSchema = (t) =>
    Yup.object().shape({
    paymentMethod: Yup.string()
      .required(t('validation.required', { field: t('paymentMethod') })),

    bankTransactionNumber: Yup.string().when('paymentMethod', {
      is: (val) =>
        [
          'CARDTOCARD',
          'MOBILEBANKINGDEPOSIT',
          'ACCOUNTTOACCOUNTRECEIPT',
          'POLDEPOSIT',
          'SHEBADEPOSIT'
        ].includes(val),
      then: (schema) =>
        schema.trim().required(t('validation.required', { field: t('trackingNumber') })),
      otherwise: (schema) => schema.notRequired()
    }),

    metadata: Yup.string().when('paymentMethod', {
      is: 'CARDTOCARD',
      then: () =>
        CARD_NUMBER_YUP_OBJECT(t).required(t('validation.required')),
      otherwise: (schema) =>
        schema.when('paymentMethod', {
          is: 'MOBILEBANKINGDEPOSIT',
          then: () =>
            Yup.string()
              .matches(/^\d+$/, t('validation.onlyNumbers'))
              .required(t('validation.required')),
          otherwise: (s) =>
            s.when('paymentMethod', {
              is: 'ACCOUNTTOACCOUNTRECEIPT',
              then: (sch) => sch.required(t('validation.required')),
              otherwise: (schma) => schma.notRequired()
            })
        })
    }),

    date: Yup.date()
      .nullable()
      .required(t('validation.required', { field: t('Date') })),

    description: Yup.string().nullable().notRequired()
  });

export const increaseProviderCreditValidationSchema = (t) => {
  const BANK_OPPONENT_TYPE = 2;

  return Yup.object().shape(
    {
      date: DATE_REQUIRED_YUP_OBJECT(t),
      serviceProviderId: STRING_REQUIRED_YUP_OBJECT(t),
      creditCurrencyCode: STRING_REQUIRED_YUP_OBJECT(t),
      creditAmount: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
      opponentType: NUMBER_REQUIRED_YUP_OBJECT(t),
      opponentId: STRING_REQUIRED_YUP_OBJECT(t),
      bankTransactionDate: RELATIVE_DATE_YUP_OBJECT(
        'opponentType',
        BANK_OPPONENT_TYPE,
        PAST_DATE_REQUIRED_YUP_OBJECT(t),
        PAST_DATE_YUP_OBJECT(t)
      ),
      bankTransactionNumber: RELATIVE_STRING_YUP_OBJECT(
        ['opponentType', 'bankDocumentId'],
        (opponentType, bankDocumentId) =>
          opponentType === BANK_OPPONENT_TYPE && !bankDocumentId,
        STRING_REQUIRED_YUP_OBJECT(t),
        undefined
      ),
      bankDocumentId: RELATIVE_STRING_YUP_OBJECT(
        ['opponentType', 'bankTransactionNumber'],
        (opponentType, bankTransactionNumber) =>
          opponentType === BANK_OPPONENT_TYPE && !bankTransactionNumber,
        STRING_REQUIRED_YUP_OBJECT(t),
        undefined
      )
    },
    [['bankTransactionNumber', 'bankDocumentId']]
  );
};

export const companyRegistrationBasicInfoValidationSchema = (
  t,
  isUserNameValid,
  hasUserNameChecked
) =>
  Yup.object({
    name: STRING_REQUIRED_YUP_OBJECT(t),
    website: WEBSITE_YUP_OBJECT(t),
    email: EMAIL_YUP_OBJECT(t),
    companyRegistrationDate: PAST_DATE_YUP_OBJECT(t),
    contactDetails: Yup.array().of(
      Yup.object({
        phoneNumber: PHONE_NUMBER_YUP_OBJECT(t)
      })
    ),
    userName: Yup.string().test(
      'isUserNameValid',
      ({ value }) =>
        value ? t(EXISTED_USERNAME_MESSAGE) : t(REQUIRED_MESSAGE),
      () => (hasUserNameChecked ? isUserNameValid : true)
    )
  });

export const companyRegistrationContactDetailsValidationSchema = (t) =>
  Yup.object({
    phoneNumber: PHONE_NUMBER_REQUIRED_YUP_OBJECT(t)
  });

export const companyRegistrationShareholdersStepValidationSchema = (t) =>
  Yup.object({
    position: STRING_REQUIRED_YUP_OBJECT(t),
    shareRate: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t).max(
      100,
      t(MAX_NUMBER_IS_100_MESSAGE)
    )
  });

export const draftSubmissionValidationSchema = (t, isIncoming = false) => {
  const BRANCH_OPPONENT_TYPE = 2;
  const BANK_OPPONENT_TYPE = 3;

  return Yup.object().shape(
    {
      date: PAST_DATE_REQUIRED_YUP_OBJECT(t),
      bankReceiptDate: RELATIVE_DATE_YUP_OBJECT(
        'opponentType',
        BANK_OPPONENT_TYPE,
        PAST_DATE_REQUIRED_YUP_OBJECT(t),
        PAST_DATE_YUP_OBJECT(t)
      ),
      branchCode: STRING_REQUIRED_YUP_OBJECT(t),
      outgoingDraftNumber: !isIncoming
        ? NUMBER_REQUIRED_YUP_OBJECT(t)
        : RELATIVE_STRING_YUP_OBJECT(
            'opponentType',
            BRANCH_OPPONENT_TYPE,
            STRING_REQUIRED_YUP_OBJECT(t)
          ),
      incomingDraftNumber: isIncoming
        ? NUMBER_REQUIRED_YUP_OBJECT(t)
        : RELATIVE_STRING_YUP_OBJECT(
            'opponentType',
            BRANCH_OPPONENT_TYPE,
            STRING_REQUIRED_YUP_OBJECT(t)
          ),
      senderId: RELATIVE_STRING_YUP_OBJECT(
        'senderFullName',
        (senderFullName) => !senderFullName,
        STRING_REQUIRED_YUP_OBJECT(t)
      ),
      senderFullName: RELATIVE_STRING_YUP_OBJECT(
        'senderId',
        (senderId) => !senderId,
        STRING_REQUIRED_YUP_OBJECT(t)
      ),
      receiverId: RELATIVE_STRING_YUP_OBJECT(
        'receiverFullName',
        (receiverFullName) => !receiverFullName,
        STRING_REQUIRED_YUP_OBJECT(t)
      ),
      receiverFullName: RELATIVE_STRING_YUP_OBJECT(
        'receiverId',
        (receiverId) => !receiverId,
        STRING_REQUIRED_YUP_OBJECT(t)
      ),
      draftAmount: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
      draftCurrencyCode: STRING_REQUIRED_YUP_OBJECT(t),
      tradeRate: isIncoming
        ? RELATIVE_NUMBER_YUP_OBJECT(
            'isUnexecuted',
            false,
            POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t)
          )
        : POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
      receivedAmount: isIncoming
        ? RELATIVE_NUMBER_YUP_OBJECT(
            'isUnexecuted',
            false,
            POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t)
          )
        : POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
      opponentId: STRING_REQUIRED_YUP_OBJECT(t),
      otherNumber: ONLY_NUMBER_AND_ENGLISH_LETTERS_YUP_OBJECT(t),
      commissionAmount: isIncoming
        ? RELATIVE_NUMBER_YUP_OBJECT(
            'isUnexecuted',
            false,
            POSITIVE_AMOUNT_YUP_OBJECT(t)
          )
        : POSITIVE_AMOUNT_YUP_OBJECT(t),
      branchCommissionAmount: POSITIVE_AMOUNT_YUP_OBJECT(t),
      receiptNumber: RELATIVE_STRING_YUP_OBJECT(
        ['opponentType', 'documentId'],
        (opponentType, documentId) =>
          opponentType === BANK_OPPONENT_TYPE && !documentId,
        STRING_REQUIRED_YUP_OBJECT(t)
      ),
      documentId: RELATIVE_STRING_YUP_OBJECT(
        ['opponentType', 'receiptNumber'],
        (opponentType, receiptNumber) =>
          opponentType === BANK_OPPONENT_TYPE && !receiptNumber,
        STRING_REQUIRED_YUP_OBJECT(t)
      )
    },
    [
      ['senderId', 'senderFullName'],
      ['receiverId', 'receiverFullName'],
      ['receiptNumber', 'documentId']
    ]
  );
};
export const mirrorIncomingDraftSubmissionValidationSchema = (t) =>
  Yup.object({
    date: PAST_DATE_REQUIRED_YUP_OBJECT(t),
    senderFullName: STRING_REQUIRED_YUP_OBJECT(t),
    receiverFullName: STRING_REQUIRED_YUP_OBJECT(t),
    draftAmount: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
    draftCurrencyCode: STRING_REQUIRED_YUP_OBJECT(t)
  });

export const miscellaneousDraftSubmissionValidationSchema = (t) =>
  Yup.object().shape(
    {
      date: PAST_DATE_REQUIRED_YUP_OBJECT(t),
      draftNumber: ONLY_NUMBER_AND_ENGLISH_LETTERS_YUP_OBJECT(t).required(
        t(REQUIRED_MESSAGE)
      ),
      senderId: RELATIVE_STRING_YUP_OBJECT(
        'senderFullName',
        (senderFullName) => !senderFullName,
        STRING_REQUIRED_YUP_OBJECT(t)
      ),
      senderFullName: RELATIVE_STRING_YUP_OBJECT(
        'senderId',
        (senderId) => !senderId,
        STRING_REQUIRED_YUP_OBJECT(t)
      ),
      receiverId: RELATIVE_STRING_YUP_OBJECT(
        'receiverFullName',
        (receiverFullName) => !receiverFullName,
        STRING_REQUIRED_YUP_OBJECT(t)
      ),
      receiverFullName: RELATIVE_STRING_YUP_OBJECT(
        'receiverId',
        (receiverId) => !receiverId,
        STRING_REQUIRED_YUP_OBJECT(t)
      ),
      draftAmount: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
      draftCurrencyCode: STRING_REQUIRED_YUP_OBJECT(t),
      tradeRate: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
      receivedAmount: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
      receivedCurrencyCode: STRING_REQUIRED_YUP_OBJECT(t),
      debtorAccountNumber: STRING_REQUIRED_YUP_OBJECT(t),
      creditorAccountNumber: STRING_REQUIRED_YUP_OBJECT(t),
      creditorAmount: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
      creditorCurrencyCode: STRING_REQUIRED_YUP_OBJECT(t),
      creditorTradeRate: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
      commissionAmount: POSITIVE_AMOUNT_YUP_OBJECT(t)
    },
    [
      ['senderId', 'senderFullName'],
      ['receiverId', 'receiverFullName']
    ]
  );

export const draftConductionValidationSchema = (t) => {
  const BRANCH_OPPONENT_TYPE = 2;
  const BANK_OPPONENT_TYPE = 3;

  return Yup.object().shape(
    {
      draftOrderId: STRING_REQUIRED_YUP_OBJECT(t),
      date: PAST_DATE_REQUIRED_YUP_OBJECT(t),
      bankReceiptDate: RELATIVE_DATE_YUP_OBJECT(
        'opponentType',
        BANK_OPPONENT_TYPE,
        PAST_DATE_REQUIRED_YUP_OBJECT(t),
        PAST_DATE_YUP_OBJECT(t)
      ),
      tradeRate: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
      receivedAmount: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
      receivedCurrencyCode: STRING_REQUIRED_YUP_OBJECT(t),
      opponentId: STRING_REQUIRED_YUP_OBJECT(t),
      commissionAmount: POSITIVE_AMOUNT_YUP_OBJECT(t),
      outgoingDraftNumber: RELATIVE_STRING_YUP_OBJECT(
        'opponentType',
        BRANCH_OPPONENT_TYPE,
        STRING_REQUIRED_YUP_OBJECT(t)
      ),
      receiptNumber: RELATIVE_STRING_YUP_OBJECT(
        ['opponentType', 'documentId'],
        (opponentType, documentId) =>
          opponentType === BANK_OPPONENT_TYPE && !documentId,
        STRING_REQUIRED_YUP_OBJECT(t)
      ),
      documentId: RELATIVE_STRING_YUP_OBJECT(
        ['opponentType', 'receiptNumber'],
        (opponentType, receiptNumber) =>
          opponentType === BANK_OPPONENT_TYPE && !receiptNumber,
        STRING_REQUIRED_YUP_OBJECT(t)
      )
    },
    [['receiptNumber', 'documentId']]
  ).test(
    // The conducted draft keeps its order's currency and amount in the form
    // (`draftCurrencyCode` / `draftAmount`), so the shared triplex rule applies
    // to the redesigned conduction screens exactly as it does to draft
    // submission. The legacy conduction pages that share this schema never put
    // those two fields in their form values, so the test is inert for them.
    sameCurrencyAmountConsistencyTest({
      t,
      initialCurrencyField: 'draftCurrencyCode',
      initialAmountField: 'draftAmount',
      equivalentCurrencyField: 'receivedCurrencyCode',
      equivalentAmountField: 'receivedAmount',
      errorPath: 'receivedAmount'
    })
  );
};

export const branchRegistrationFirstStepValidationSchema = (
  t,
  isTelegramEnabled = false
) =>
  Yup.object({
    branchName: STRING_REQUIRED_YUP_OBJECT(t),
    currencyCode: STRING_REQUIRED_YUP_OBJECT(t),
    email: EMAIL_YUP_OBJECT(t),
    phoneNumbers: Yup.array().of(
      Yup.object({
        phoneNumber: PHONE_NUMBER_YUP_OBJECT(t)
      })
    ),
    whatsappGroupLinks: Yup.array().of(
      Yup.string()
        .nullable()
        .matches(
          isTelegramEnabled
            ? /^-?\d{5,20}$/
            : /^https:\/\/chat\.whatsapp\.com\/(?:invite\/)?([0-9A-Za-z]{22})$/,
          {
            message: t('validation.invalidLink'),
            excludeEmptyString: true // Allows empty string if the field is optional
          }
        )
    ),
    agentFirstName: STRING_REQUIRED_YUP_OBJECT(t),
    agentLastName: STRING_REQUIRED_YUP_OBJECT(t)
  });
export const branchRegistrationAdditionalInfoValidationSchema = (t) =>
  Yup.object({
    idNumber: Yup.string()
      .min(6, t(NATIONAL_ID_MINIMUM_LENGTH))
      .max(15, t(NATIONAL_ID_MAXIMUM_LENGTH)),
    zipCode: Yup.string()
      .min(4, t(POSTAL_CODE_MINIMUM_LENGTH))
      .max(10, t(POSTAL_CODE_MAXIMUM_LENGTH)),
    registrationNumber: Yup.string()
      .min(5, t(REGISTRATION_CODE_MINIMUM_LENGTH))
      .max(15, t(REGISTRATION_CODE_MAXIMUM_LENGTH))
  });
export const branchRegistrationThirdStepValidationSchema = (t) =>
  Yup.object({
    name: STRING_REQUIRED_YUP_OBJECT(t),
    businessName: STRING_REQUIRED_YUP_OBJECT(t),
    agentName: STRING_REQUIRED_YUP_OBJECT(t),
    address: STRING_REQUIRED_YUP_OBJECT(t),
    phoneNumbers: Yup.array().of(
      Yup.object({
        phoneNumber: PHONE_NUMBER_REQUIRED_YUP_OBJECT(t)
      })
    )
  });

export const paymentOrderValidationSchema = (t) =>
  Yup.object({
    accountHolderName: STRING_REQUIRED_YUP_OBJECT(t),
    cardNumber: CARD_NUMBER_YUP_OBJECT(t).concat(STRING_REQUIRED_YUP_OBJECT(t)),
    shebaNumber: IBAN_YUP_OBJECT(t),
    bankName: STRING_REQUIRED_YUP_OBJECT(t),
    currencyCode: STRING_REQUIRED_YUP_OBJECT(t),
    amount: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
    accountId: STRING_REQUIRED_YUP_OBJECT(t),
    date: DATE_REQUIRED_YUP_OBJECT(t),
    telegramGroupId: STRING_REQUIRED_YUP_OBJECT(t)
  });

export const executePaymentOrderValidationSchema = (t) =>
  Yup.object({
    date: PAST_EXACT_DATE_REQUIRED_YUP_OBJECT(t),
    receiptId: NUMBER_REQUIRED_YUP_OBJECT(t)
  });
export const submitTransactionTypeValidationSchema = (t) =>
  Yup.object({
    transactionType: STRING_REQUIRED_YUP_OBJECT(t)
  });
export const documentAssignmentModalValidationSchema = (t) =>
  Yup.object({
    opponentAccountId: STRING_REQUIRED_YUP_OBJECT(t)
  });
export const convertValidationSchema = (t, selectedTransactions) =>
  Yup.object({
    destinationCurrencyCode: Yup.string().required(
      t('Destination currency is required')
    ),

    rate: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),

    commissionAmount: Yup.number()
      .nullable()
      .min(0, t('Commission cannot be negative'))
      .when('isCurrency', {
        is: true,
        then: (schema) =>
          schema.test(
            'commission-less-than-draft',
            t('Commission must be less than transaction amount'),
            (value) => {
              if (!value || !selectedTransactions?.length) return true;

              return !selectedTransactions.some((tx) => value > tx.draftAmount);
            }
          )
      })
      .when('isCurrency', {
        is: false,
        then: (schema) =>
          schema
            .min(0, t('Commission percent cannot be negative'))
            .max(100, t('Commission percent cannot exceed 100'))
      }),

    commissionCurrencyCode: Yup.string()
      .nullable()
      .when('commissionAmount', {
        is: (val) => val !== undefined && val !== null && val !== '',
        then: (schema) => schema.required(t('Commission currency is required'))
      }),

    isCurrency: Yup.boolean(),

    branchCommissionIsDebtor: Yup.boolean()
  });
export const excelFileReportValidationSchema = (t) =>
  Yup.object({
    // currencyCode: STRING_REQUIRED_YUP_OBJECT(t),
    reportPeriod: STRING_REQUIRED_YUP_OBJECT(t),
    fromDate: DATE_REQUIRED_YUP_OBJECT(t),
    toDate: DATE_REQUIRED_YUP_OBJECT(t)
    // prePay: STRING_REQUIRED_YUP_OBJECT(t),
    // afghanistanBank: STRING_REQUIRED_YUP_OBJECT(t),
    // prePurchaseContract: STRING_REQUIRED_YUP_OBJECT(t),
    // totalValueOfOtherAssets: STRING_REQUIRED_YUP_OBJECT(t),
    // tangibleAssets: STRING_REQUIRED_YUP_OBJECT(t),
    // reductionInDepreciationOfTangibleAssets: STRING_REQUIRED_YUP_OBJECT(t),
    // intangibleAssets: STRING_REQUIRED_YUP_OBJECT(t),
    // reductionInDepreciationOfIntangibleAssets: STRING_REQUIRED_YUP_OBJECT(t),
    // billPayable: STRING_REQUIRED_YUP_OBJECT(t),
    // taxPayable: STRING_REQUIRED_YUP_OBJECT(t),
    // otherDebt: STRING_REQUIRED_YUP_OBJECT(t),
    // preSaleContracts: STRING_REQUIRED_YUP_OBJECT(t),
    // otherRevenue: STRING_REQUIRED_YUP_OBJECT(t),
    // administrativeExpenses: STRING_REQUIRED_YUP_OBJECT(t),
    // depreciationExpenses: STRING_REQUIRED_YUP_OBJECT(t),
    // telephoneExpenses: STRING_REQUIRED_YUP_OBJECT(t),
    // rentalExpenses: STRING_REQUIRED_YUP_OBJECT(t),
    // insuranceExpenses: STRING_REQUIRED_YUP_OBJECT(t),
    // licensingExpenses: STRING_REQUIRED_YUP_OBJECT(t),
    // employeeExpenses: STRING_REQUIRED_YUP_OBJECT(t),
    // otherExpenses: STRING_REQUIRED_YUP_OBJECT(t),
    // totalShareholdersEquity: STRING_REQUIRED_YUP_OBJECT(t)
  });

export const BonCardRequestValidationSchemas = (t) =>
  Yup.object({
    addressId: STRING_REQUIRED_YUP_OBJECT(t),
    documentId: STRING_REQUIRED_YUP_OBJECT(t),
    residenceNumber: STRING_REQUIRED_YUP_OBJECT(t),
    documentFileId: STRING_REQUIRED_YUP_OBJECT(t),
    phoneNumberId: STRING_REQUIRED_YUP_OBJECT(t),
    documentType: STRING_REQUIRED_YUP_OBJECT(t),
    profileImageId: STRING_REQUIRED_YUP_OBJECT(t),
    fatherName: STRING_REQUIRED_YUP_OBJECT(t),
    birthDate: STRING_REQUIRED_YUP_OBJECT(t),
    documentIdUi: STRING_REQUIRED_YUP_OBJECT(t)
  });

export const RejectPendDraftsValidationSchemas = (t) =>
  Yup.object({
    draftIds: Yup.array().of(STRING_REQUIRED_YUP_OBJECT(t)),
    reason: STRING_REQUIRED_YUP_OBJECT(t)
  });

export const addPaymentPlaceValidationSchema = (t) =>
  Yup.object({
    paymentPlaceId: STRING_REQUIRED_YUP_OBJECT(t),
    rates: Yup.array().of(
      Yup.object({
        rate: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
        currency: STRING_REQUIRED_YUP_OBJECT(t)
      })
    )
  });

export const rowBasedFilterValidationSchema = (t) =>
  Yup.object().shape(
    {
      branchId: RELATIVE_STRING_YUP_OBJECT(
        ['fromNumber', 'toNumber'],
        (fromNumber, toNumber) => !!fromNumber || !!toNumber,
        STRING_REQUIRED_YUP_OBJECT(t)
      )
      // toNumber: RELATIVE_NUMBER_YUP_OBJECT(
      //   ['fromNumber', 'toNumber'],
      //   (fromNumber, toNumber) =>
      //   (toNumber !== null) && (toNumber < fromNumber),
      //   NUMBER_GREATER_YUP_OBJECT(
      //     t,
      //     NUMBER_MUST_BE_GREATER_THAN_OTHER_NUMBER_MESSAGE
      //   )
      // )
    },
    [['fromNumber', 'toNumber']]
  );

export const pendingDraftsFilterValidationSchema = (t) =>
  Yup.object({
    toDate: DATE_REQUIRED_YUP_OBJECT(t)
  });

/* ***************** logical validations ********************* */
export const validateCashTransactionLogical = (
  values,
  setFieldError,
  t,
  IsNewForm
) =>
  checkCommissionLogical({
    values,
    setFieldError,
    t,
    IsNewForm,
    hasCommission: 'hasCommission',
    commissionAmount: 'commissionAmount',
    commissionCurrencyCode: 'commissionCurrencyCode',
    amount: 'amount',
    currencyCode: 'currencyCode'
  });

export const validateBankTransactionLogical = (values, setFieldError, t) => {
  let isValid = true;
  let isValidAmount = true;
  let error = null;
  const newErrorsLength = values.depositInfos.length;
  const newErrors = [...Array(newErrorsLength).keys()].map(() => ({
    commissionAmount: ''
  }));
  if (values.isKnown) {
    if (values.amountsSum > values.totalAmount) {
      isValidAmount = false;
      isValid = false;
      error = t('validation.transactionAmountLessThanSum');
    } else if (values.amountsSum < values.totalAmount) {
      isValidAmount = false;
      isValid = false;
      error = t('validation.transactionAmountMoreThanSum');
    }
  }
  values.depositInfos.forEach((depositInfo, index) => {
    // if (
    //   values.isKnown &&
    //   values.hasCommissions[index] &&
    //   (!depositInfo.commissionAmount || !depositInfo.commissionCurrencyCode)
    // ) {
    //   newErrors[index].commissionAmount = t(UNCHECK_MESSAGE);
    //   isValid = false;
    // }
    if (
      values.isKnown &&
      values.hasCommissions[index] &&
      depositInfo.commissionCurrencyCode === depositInfo.currencyCode &&
      depositInfo.commissionAmount >= depositInfo.amount
    ) {
      newErrors[index].commissionAmount = t(
        COMMISSION_LOWER_THAT_AMOUNT_MESSAGE
      );
      isValid = false;
    }
  });
  if (!isValid) {
    if (!isValidAmount) {
      setFieldError('totalAmount', error);
    } else {
      setFieldError('depositInfos', newErrors);
    }
  }
  return isValid;
};

export const validateTransferBetweenAccountsLogical = (
  values,
  setFieldError,
  t,
  IsNewForm
) => {
  const debtorCommissionLogicalValidationResult = checkCommissionLogical({
    values,
    setFieldError,
    t,
    IsNewForm,
    hasCommission: 'hasDebtorCommission',
    commissionAmount: 'debtorCommissionAmount',
    commissionCurrencyCode: 'debtorCommissionCurrencyCode',
    amount: 'amount',
    currencyCode: 'currencyCode'
  });

  const creditorCommissionLogicalValidationResult = checkCommissionLogical({
    values,
    setFieldError,
    t,
    IsNewForm,
    hasCommission: 'hasCreditorCommission',
    commissionAmount: 'creditorCommissionAmount',
    commissionCurrencyCode: 'creditorCommissionCurrencyCode',
    amount: 'amount',
    currencyCode: 'currencyCode'
  });
  return (
    debtorCommissionLogicalValidationResult &&
    creditorCommissionLogicalValidationResult
  );
};

export const validateSubmitChequeLogical = (values, setFieldError, t) => {
  const assignorCommissionLogicalValidationResult = checkCommissionLogical({
    values,
    setFieldError,
    t,
    hasCommission: 'hasAssignorCommission',
    commissionAmount: 'assignorCommissionAmount',
    commissionCurrencyCode: 'assignorCommissionCurrencyCode',
    amount: 'amount',
    currencyCode: 'currencyCode'
  });

  const receiverCommissionLogicalValidationResult = checkCommissionLogical({
    values,
    setFieldError,
    t,
    hasCommission: 'hasReceiverCommission',
    commissionAmount: 'receiverCommissionAmount',
    commissionCurrencyCode: 'receiverCommissionCurrencyCode',
    amount: 'amount',
    currencyCode: 'currencyCode'
  });
  const dueDateValidationResult = compareTwoDates({
    values,
    setFieldError,
    t,
    biggerDate: 'dueDate',
    smallerDate: 'date',
    message: DUE_DATE_BIGGER_THAN_DATE_MESSAGE,
    errorOnBiggerDate: true
  });
  return (
    assignorCommissionLogicalValidationResult &&
    receiverCommissionLogicalValidationResult &&
    dueDateValidationResult
  );
};

export const validateTradeCurrencyLogical = (
  values,
  setFieldError,
  t,
  IsNewForm
) => {
  const buyCommissionLogicalValidationResult = checkCommissionLogical({
    values,
    setFieldError,
    t,
    hasCommission: 'hasBuyCommission',
    commissionAmount: 'buyCommissionAmount',
    commissionCurrencyCode: 'buyCommissionCurrencyCode',
    amount: 'buyAmount',
    currencyCode: 'buyCurrencyCode',
    IsNewForm
  });
  const sellCommissionLogicalValidationResult = checkCommissionLogical({
    values,
    setFieldError,
    t,
    hasCommission: 'hasSellCommission',
    commissionAmount: 'sellCommissionAmount',
    commissionCurrencyCode: 'sellCommissionCurrencyCode',
    amount: 'sellAmount',
    currencyCode: 'sellCurrencyCode',
    IsNewForm
  });
  const isTradeRateValid = checkTradeRateValidation({
    values,
    setFieldError,
    tradeRate: 'tradeRate',
    isTradeRateValid: 'isTradeRateValid',
    t
  });
  return (
    buyCommissionLogicalValidationResult &&
    sellCommissionLogicalValidationResult &&
    isTradeRateValid
  );
};

export const validateChequeOperationLogical = (values, setFieldError, t) => {
  const assignorCommissionLogicalValidationResult = checkCommissionLogical({
    values,
    setFieldError,
    t,
    hasCommission: 'hasAssignorCommission',
    commissionAmount: 'assignorCommissionAmount',
    commissionCurrencyCode: 'assignorCommissionCurrencyCode',
    amount: 'amount',
    currencyCode: 'currencyCode'
  });
  const receiverCommissionLogicalValidationResult = checkCommissionLogical({
    values,
    setFieldError,
    t,
    hasCommission: 'hasReceiverCommission',
    commissionAmount: 'receiverCommissionAmount',
    commissionCurrencyCode: 'receiverCommissionCurrencyCode',
    amount: 'amount',
    currencyCode: 'currencyCode'
  });
  return (
    assignorCommissionLogicalValidationResult &&
    receiverCommissionLogicalValidationResult
  );
};

export const validatePersonRegistrationPersonalDetailsLogical = (
  values,
  setFieldError,
  t
) => {
  const twoDateDifferencesInDay =
    values.birthDate &&
    calculateTwoDatesDifferencesInDays(new Date(), values.birthDate);
  if (values.birthDate && twoDateDifferencesInDay < 10 * 365) {
    setFieldError('birthDate', t(CUSTOMER_AGE_LIMIT_MESSAGE));
    return false;
  }
  return true;
};

export const validatePersonRegistrationDocumentsStepLogical = (
  values,
  setFieldError,
  t
) => {
  // if (!values.issueDate && values.expiryDate) {
  //   setFieldError('issueDate', t(REQUIRED_MESSAGE));
  //   return false;
  // }
  // if (values.issueDate && !values.expiryDate) {
  //   setFieldError('expiryDate', t(REQUIRED_MESSAGE));
  //   return false;
  // }
  const isIssueDateValid =
    !values.issueDate && !values.expiryDate
      ? true
      : compareTwoDates({
          values: {
            ...values,
            today: new Date(+getMoonUtcTime(new Date())),
            issueDate: new Date(+getMoonUtcTime(values.issueDate))
          },
          setFieldError,
          biggerDate: 'today',
          smallerDate: 'issueDate',
          message: ISSUE_DATE_CAN_NOT_BE_GREATER_THAN_TODAY_MESSAGE,
          t,
          errorOnBiggerDate: false
        });
  if (values.issueDate && values.expiryDate) {
    const documentValidationRangeInDays = calculateTwoDatesDifferencesInDays(
      values.expiryDate,
      values.issueDate
    );
    if (documentValidationRangeInDays < 1) {
      setFieldError(
        'expiryDate',
        t(EXPIRY_DATE_MUST_BE_GREATER_THAN_ISSUE_DATE_MESSAGE)
      );
      return false;
    }
  }
  return isIssueDateValid;
};

export const validateDraftSubmissionLogical = (
  values,
  setFieldError,
  t,
  IsNewForm
) => {
  const isDraftCommissionValid = checkCommissionLogical({
    values,
    setFieldError,
    hasCommission: 'commissionAmount',
    commissionAmount: 'commissionAmount',
    commissionCurrencyCode: 'commissionCurrencyCode',
    amount: 'receivedAmount',
    currencyCode: 'receivedCurrencyCode',
    isAlwaysChecked: true,
    t,
    IsNewForm
  });
  const isBranchCommissionValid = checkCommissionLogical({
    values,
    setFieldError,
    hasCommission: 'branchCommissionAmount',
    commissionAmount: 'branchCommissionAmount',
    commissionCurrencyCode: 'branchCommissionCurrencyCode',
    amount: 'draftAmount',
    currencyCode: 'draftCurrencyCode',
    t,
    IsNewForm
  });
  const isTradeRateValid = values.isUnexecuted
    ? true
    : checkTradeRateValidation({
        values,
        setFieldError,
        tradeRate: 'tradeRate',
        isTradeRateValid: 'isTradeRateValid',
        t
      });
  return isDraftCommissionValid && isBranchCommissionValid && isTradeRateValid;
};

export const validateConvertLogical = (values, setFieldError, t) => {
  const isTradeRateValid = checkTradeRateValidation({
    values,
    setFieldError,
    tradeRate: 'rate',
    isTradeRateValid: 'isTradeRateValid',
    t
  });
  return isTradeRateValid;
};

export const validateMiscellaneousDraftSubmissionLogical = (
  values,
  setFieldError,
  t
) => {
  const isTradeRateValid = checkTradeRateValidation({
    values,
    setFieldError,
    tradeRate: 'tradeRate',
    isTradeRateValid: 'isTradeRateValid',
    t
  });
  const isCreditorTradeRateValid = checkTradeRateValidation({
    values,
    setFieldError,
    tradeRate: 'creditorTradeRate',
    isTradeRateValid: 'isCreditorTradeRateValid',
    t
  });
  const isCommissionValid = checkCommissionLogical({
    values,
    setFieldError,
    commissionAmount: 'commissionAmount',
    commissionCurrencyCode: 'commissionCurrencyCode',
    amount: 'receivedAmount',
    currencyCode: 'receivedCurrencyCode',
    isAlwaysChecked: true,
    t
  });

  return isCommissionValid && isTradeRateValid && isCreditorTradeRateValid;
};

export const validateDraftConductionLogical = (values, setFieldError, t) => {
  const isCommissionValid = checkCommissionLogical({
    values,
    setFieldError,
    commissionAmount: 'commissionAmount',
    commissionCurrencyCode: 'commissionCurrencyCode',
    amount: 'receivedAmount',
    currencyCode: 'receivedCurrencyCode',
    isAlwaysChecked: true,
    t
  });

  const isTradeRateValid = checkTradeRateValidation({
    values,
    setFieldError,
    tradeRate: 'tradeRate',
    isTradeRateValid: 'isTradeRateValid',
    t
  });
  return isCommissionValid && isTradeRateValid;
};

export const validatePaymentOrderDepositLogical = (
  values,
  setFieldError,
  t
) => {
  const sumDeposits = values.paymentReceipts
    .map(({ amount }) => amount)
    .reduce((partialSum, a) => partialSum + a, 0);
  if (sumDeposits > values.amount) {
    const errorsArray = new Array(values.currentIndex).fill(undefined);
    errorsArray[values.currentIndex] = {
      amount: t(NOT_MORE_AMOUNT_PAYMENT_ORDER)
    };
    setFieldError('paymentReceipts', errorsArray);
  }
};

export const acceptOrDeclineCustomerRegistrationBySupportValidationSchema = (
  t
) =>
  Yup.object({
    description: STRING_REQUIRED_YUP_OBJECT(t)
  });

export const submitOutgoingDraftBasicInformationValidation = (
  t,
  isIncoming = false
) =>
  Yup.object({
    date: PAST_DATE_REQUIRED_YUP_OBJECT(t),
    senderFullName: STRING_REQUIRED_YUP_OBJECT(t),
    receiverFullName: STRING_REQUIRED_YUP_OBJECT(t),
    branchCode: STRING_REQUIRED_YUP_OBJECT(t),
    outgoingDraftNumber: !isIncoming && NUMBER_REQUIRED_YUP_OBJECT(t),
    incomingDraftNumber: isIncoming && NUMBER_REQUIRED_YUP_OBJECT(t)
  });
export const submitSettlementDraftBasicInformationValidation = (t) =>
  Yup.object({
    basicInformation: Yup.object({
      date: PAST_DATE_REQUIRED_YUP_OBJECT(t),
      senderFullName: STRING_REQUIRED_YUP_OBJECT(t),
      receiverFullName: STRING_REQUIRED_YUP_OBJECT(t),
      branchCode: STRING_REQUIRED_YUP_OBJECT(t),
      draftOrderNumber: NUMBER_REQUIRED_YUP_OBJECT(t)
    })
  });

export const submitMiscDraftBasicInformationValidation = (t) =>
  Yup.object({
    date: PAST_DATE_REQUIRED_YUP_OBJECT(t),
    senderFullName: STRING_REQUIRED_YUP_OBJECT(t),
    receiverFullName: STRING_REQUIRED_YUP_OBJECT(t)
  });

export const cancelOutgoingDraftValidation = (t) => {
  const BANK_OPPONENT_TYPE = 3;
  const BRANCH_OPPONENT_TYPE = 2;

  return Yup.object().shape(
    {
      date: PAST_DATE_REQUIRED_YUP_OBJECT(t),
      incomingDraftNumber: NUMBER_REQUIRED_YUP_OBJECT(t),
      outgoingDraftNumber: RELATIVE_STRING_YUP_OBJECT(
        'opponentType',
        BRANCH_OPPONENT_TYPE,
        STRING_REQUIRED_YUP_OBJECT(t)
      ),
      opponentId: STRING_REQUIRED_YUP_OBJECT(t),
      bankReceiptDate: RELATIVE_DATE_YUP_OBJECT(
        'opponentType',
        BANK_OPPONENT_TYPE,
        PAST_DATE_REQUIRED_YUP_OBJECT(t),
        PAST_DATE_YUP_OBJECT(t)
      ),
      receiptNumber: RELATIVE_STRING_YUP_OBJECT(
        ['opponentType', 'documentId'],
        (opponentType, documentId) =>
          opponentType === BANK_OPPONENT_TYPE && !documentId,
        STRING_REQUIRED_YUP_OBJECT(t)
      ),
      documentId: RELATIVE_STRING_YUP_OBJECT(
        ['opponentType', 'receiptNumber'],
        (opponentType, receiptNumber) =>
          opponentType === BANK_OPPONENT_TYPE && !receiptNumber,
        STRING_REQUIRED_YUP_OBJECT(t)
      )
    },
    [['receiptNumber', 'documentId']]
  );
};

export const cancelIncomingDraftValidation = (t) => {
  const BANK_OPPONENT_TYPE = 3;
  const BRANCH_OPPONENT_TYPE = 2;

  return Yup.object().shape(
    {
      date: PAST_DATE_REQUIRED_YUP_OBJECT(t),
      outgoingDraftNumber: NUMBER_REQUIRED_YUP_OBJECT(t),
      incmoingDraftNumber: RELATIVE_STRING_YUP_OBJECT(
        'opponentType',
        BRANCH_OPPONENT_TYPE,
        STRING_REQUIRED_YUP_OBJECT(t)
      ),
      bankReceiptDate: RELATIVE_DATE_YUP_OBJECT(
        'opponentType',
        BANK_OPPONENT_TYPE,
        PAST_DATE_REQUIRED_YUP_OBJECT(t),
        PAST_DATE_YUP_OBJECT(t)
      ),
      receiptNumber: RELATIVE_STRING_YUP_OBJECT(
        ['opponentType', 'documentId'],
        (opponentType, documentId) =>
          opponentType === BANK_OPPONENT_TYPE && !documentId,
        STRING_REQUIRED_YUP_OBJECT(t)
      ),
      documentId: RELATIVE_STRING_YUP_OBJECT(
        ['opponentType', 'receiptNumber'],
        (opponentType, receiptNumber) =>
          opponentType === BANK_OPPONENT_TYPE && !receiptNumber,
        STRING_REQUIRED_YUP_OBJECT(t)
      )
    },
    [['receiptNumber', 'documentId']]
  );
};

export const submitMiscDraftFinancialInformationMobileValidation = (t) =>
  Yup.object({
    amount: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
    currencyCode: STRING_REQUIRED_YUP_OBJECT(t),

    debtorAccountNumber: STRING_REQUIRED_YUP_OBJECT(t),
    debtorAmount: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
    debtorCurrencyCode: STRING_REQUIRED_YUP_OBJECT(t),
    debtorRate: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
    debtorCommissionAmount: POSITIVE_AMOUNT_YUP_OBJECT(t).when(
      ['debtorCommissionCurrencyCode', 'currencyCode'],
      {
        is: (debtorCommissionCurrencyCode, currencyCode) =>
          debtorCommissionCurrencyCode === currencyCode,
        then: Yup.number()
          .lessThan(Yup.ref('amount'), t(COMMISSION_LOWER_THAT_AMOUNT_MESSAGE))
          .nullable()
      }
    ),

    creditorAccountNumber: STRING_REQUIRED_YUP_OBJECT(t),
    creditorAmount: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
    creditorCurrencyCode: STRING_REQUIRED_YUP_OBJECT(t),
    creditorRate: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
    creditorCommissionAmount: POSITIVE_AMOUNT_YUP_OBJECT(t)
      .when(['creditorCommissionCurrencyCode', 'creditorCurrencyCode'], {
        is: (creditorCommissionCurrencyCode, creditorCurrencyCode) =>
          creditorCommissionCurrencyCode === creditorCurrencyCode,
        then: Yup.number()
          .lessThan(
            Yup.ref('creditorAmount'),
            t(COMMISSION_LOWER_THAT_AMOUNT_MESSAGE)
          )
          .nullable()
      })
      // Two triplexes share the same initial side here (`amount` /
      // `currencyCode`), so the shared rule is applied once per pair. Each test
      // gets its own name, otherwise Yup would keep only the last one.
      .test(
        sameCurrencyAmountConsistencyTest({
          t,
          initialCurrencyField: 'currencyCode',
          initialAmountField: 'amount',
          equivalentCurrencyField: 'debtorCurrencyCode',
          equivalentAmountField: 'debtorAmount',
          errorPath: 'financialInformation.debtorAmount'
        })
      )
  });

export const submitMiscDraftFinancialInformationValidation = (t) =>
  Yup.object({
    basicInformation: Yup.object({
      date: PAST_DATE_REQUIRED_YUP_OBJECT(t),
      senderFullName: STRING_REQUIRED_YUP_OBJECT(t),
      receiverFullName: STRING_REQUIRED_YUP_OBJECT(t)
    }),

    financialInformation: Yup.object({
      amount: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
      currencyCode: STRING_REQUIRED_YUP_OBJECT(t),
      debtorAccountNumber: STRING_REQUIRED_YUP_OBJECT(t),
      debtorAmount: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
      debtorCurrencyCode: STRING_REQUIRED_YUP_OBJECT(t),
      debtorRate: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
      debtorCommissionAmount: POSITIVE_AMOUNT_YUP_OBJECT(t).when(
        ['debtorCommissionCurrencyCode', 'currencyCode'],
        {
          is: (debtorCommissionCurrencyCode, currencyCode) =>
            debtorCommissionCurrencyCode === currencyCode,
          then: Yup.number()
            .lessThan(
              Yup.ref('amount'),
              t(COMMISSION_LOWER_THAT_AMOUNT_MESSAGE)
            )
            .nullable()
        }
      ),
      creditorAccountNumber: STRING_REQUIRED_YUP_OBJECT(t),
      creditorAmount: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
      creditorCurrencyCode: STRING_REQUIRED_YUP_OBJECT(t),
      creditorRate: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
      creditorCommissionAmount: POSITIVE_AMOUNT_YUP_OBJECT(t).when(
        ['creditorCommissionCurrencyCode', 'creditorCurrencyCode'],
        {
          is: (creditorCommissionCurrencyCode, creditorCurrencyCode) =>
            creditorCommissionCurrencyCode === creditorCurrencyCode,
          then: Yup.number()
            .lessThan(
              Yup.ref('creditorAmount'),
              t(COMMISSION_LOWER_THAT_AMOUNT_MESSAGE)
            )
            .nullable()
        }
      )
    })
      // Two triplexes share the same initial side here (`amount` /
      // `currencyCode`), so the shared rule is applied once per pair. Each test
      // gets its own name, otherwise Yup would keep only the last one.
      .test(
        sameCurrencyAmountConsistencyTest({
          t,
          initialCurrencyField: 'currencyCode',
          initialAmountField: 'amount',
          equivalentCurrencyField: 'debtorCurrencyCode',
          equivalentAmountField: 'debtorAmount',
          errorPath: 'financialInformation.debtorAmount'
        })
      )
  });

// TODO: use one validation after redesign mobile draft submission same as desktop
export const submitOutgoingDraftFinancialInformationValidation = (
  t,
  isIncoming = false
) => {
  const BANK_OPPONENT_TYPE = 3;

  return Yup.object().shape(
    {
      draftAmount: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
      draftCurrencyCode: STRING_REQUIRED_YUP_OBJECT(t),
      tradeRate: isIncoming
        ? RELATIVE_NUMBER_YUP_OBJECT(
            'isUnexecuted',
            false,
            POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t)
          )
        : POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
      bankReceiptDate: RELATIVE_DATE_YUP_OBJECT(
        'opponentType',
        BANK_OPPONENT_TYPE,
        PAST_DATE_REQUIRED_YUP_OBJECT(t),
        PAST_DATE_YUP_OBJECT(t)
      ),
      receivedAmount: isIncoming
        ? RELATIVE_NUMBER_YUP_OBJECT(
            'isUnexecuted',
            false,
            POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t)
          )
        : POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
      opponentId: STRING_REQUIRED_YUP_OBJECT(t),
      commissionAmount: POSITIVE_AMOUNT_YUP_OBJECT(t).when(
        ['commissionCurrencyCode', 'receivedCurrencyCode'],
        {
          is: (commissionCurrencyCode, receivedCurrencyCode) =>
            commissionCurrencyCode === receivedCurrencyCode,
          then: Yup.number()
            .lessThan(
              Yup.ref('receivedAmount'),
              t(COMMISSION_LOWER_THAT_AMOUNT_MESSAGE)
            )
            .nullable()
        }
      ),
      branchCommissionAmount: POSITIVE_AMOUNT_YUP_OBJECT(t).when(
        ['branchCommissionCurrencyCode', 'draftCurrencyCode'],
        {
          is: (branchCommissionCurrencyCode, draftCurrencyCode) =>
            branchCommissionCurrencyCode === draftCurrencyCode,
          then: Yup.number()
            .lessThan(
              Yup.ref('draftAmount'),
              t(COMMISSION_LOWER_THAT_AMOUNT_MESSAGE)
            )
            .nullable()
        }
      ),
      receiptNumber: RELATIVE_STRING_YUP_OBJECT(
        ['opponentType', 'documentId'],
        (opponentType, documentId) =>
          opponentType === BANK_OPPONENT_TYPE && !documentId,
        STRING_REQUIRED_YUP_OBJECT(t)
      ),
      documentId: RELATIVE_STRING_YUP_OBJECT(
        ['opponentType', 'receiptNumber'],
        (opponentType, receiptNumber) =>
          opponentType === BANK_OPPONENT_TYPE && !receiptNumber,
        STRING_REQUIRED_YUP_OBJECT(t)
      )
    },
    [['receiptNumber', 'documentId']]
  ).test(
    // Same shared triplex rule as the desktop draft submission, on the flat
    // shape the mobile screens use.
    sameCurrencyAmountConsistencyTest({
      t,
      initialCurrencyField: 'draftCurrencyCode',
      initialAmountField: 'draftAmount',
      equivalentCurrencyField: 'receivedCurrencyCode',
      equivalentAmountField: 'receivedAmount',
      errorPath: 'receivedAmount'
    })
  );
};
export const submitDraftValidation = (
  t,
  isIncoming = false,
  isUpdateMode = false,
  initialFinancialValues = null
) => {
  const BANK_OPPONENT_TYPE = 3;

  const baseTradeRateRule = isIncoming
    ? RELATIVE_NUMBER_YUP_OBJECT(
        'isUnexecuted',
        false,
        POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t)
      )
    : POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t);

  return Yup.object().shape({
    basicInformation: Yup.object().shape({
      date: PAST_DATE_REQUIRED_YUP_OBJECT(t),
      senderFullName: STRING_REQUIRED_YUP_OBJECT(t),
      receiverFullName: STRING_REQUIRED_YUP_OBJECT(t),
      branchCode: STRING_REQUIRED_YUP_OBJECT(t),
      incomingDraftNumber: isIncoming && NUMBER_REQUIRED_YUP_OBJECT(t),
      outgoingDraftNumber: !isIncoming && NUMBER_REQUIRED_YUP_OBJECT(t)
    }),

    financialInformation: Yup.object()
      .shape({
        draftAmount: !isUpdateMode
          ? POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t)
          : isIncoming
          ? Yup.number().when('isUnexecuted', {
              is: true,
              then: (schema) => schema.min(0).required(t(REQUIRED_MESSAGE)),
              otherwise: () => POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t)
            })
          : Yup.number().min(0),
        draftCurrencyCode: STRING_REQUIRED_YUP_OBJECT(t),
        tradeRate: (() => {
          if (!isUpdateMode || !initialFinancialValues) {
            return baseTradeRateRule;
          }
          return Yup.mixed().test(
            'trade-rate-valid',
            t(REQUIRED_MESSAGE),
            function (value) {
              const isUnchanged = areTriplexValuesUnchanged(
                {
                  draftAmount: this.parent?.draftAmount,
                  draftCurrencyCode: this.parent?.draftCurrencyCode,
                  receivedAmount: this.parent?.receivedAmount,
                  receivedCurrencyCode: this.parent?.receivedCurrencyCode,
                  tradeRate: value
                },
                initialFinancialValues
              );

              // If in edit mode and financial values are untouched, ignore tradeRate validation
              if (isUnchanged) {
                return true;
              }

              return baseTradeRateRule.isValidSync(value);
            }
          );
        })(),
        bankReceiptDate: RELATIVE_DATE_YUP_OBJECT(
          'opponentType',
          BANK_OPPONENT_TYPE,
          PAST_DATE_REQUIRED_YUP_OBJECT(t),
          PAST_DATE_YUP_OBJECT(t)
        ),
        receivedAmount: !isUpdateMode
          ? POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t)
          : isIncoming
          ? Yup.number().when('isUnexecuted', {
              is: true,
              then: (schema) => schema.min(0).required(t(REQUIRED_MESSAGE)),
              otherwise: () => POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t)
            })
          : Yup.number().min(0),
        opponentId: STRING_REQUIRED_YUP_OBJECT(t),
        commissionAmount: POSITIVE_AMOUNT_YUP_OBJECT(t).when(
          ['commissionCurrencyCode', 'receivedCurrencyCode'],
          {
            is: (cCode, rCode) => cCode === rCode,
            then: (schema) =>
              schema
                .lessThan(
                  Yup.ref('receivedAmount'),
                  t(COMMISSION_LOWER_THAT_AMOUNT_MESSAGE)
                )
                .nullable()
          }
        ),
        branchCommissionAmount: POSITIVE_AMOUNT_YUP_OBJECT(t).when(
          ['branchCommissionCurrencyCode', 'draftCurrencyCode'],
          {
            is: (bCode, dCode) => bCode === dCode,
            then: (schema) =>
              schema
                .lessThan(
                  Yup.ref('draftAmount'),
                  t(COMMISSION_LOWER_THAT_AMOUNT_MESSAGE)
                )
                .nullable()
          }
        ),
        receiptNumber: Yup.string().nullable(),
        documentId: Yup.string().nullable()
      })
      .test(
        'receipt-or-document-required',
        t(REQUIRED_MESSAGE),
        function (values) {
          const { opponentType, receiptNumber, documentId } = values;

          if (opponentType === BANK_OPPONENT_TYPE) {
            if (!receiptNumber && !documentId) {
              return this.createError({
                path: 'financialInformation.receiptNumber',
                message: t(REQUIRED_MESSAGE)
              });
            }
          }
          return true;
        }
      )
      .test(
        sameCurrencyAmountConsistencyTest({
          t,
          initialCurrencyField: 'draftCurrencyCode',
          initialAmountField: 'draftAmount',
          equivalentCurrencyField: 'receivedCurrencyCode',
          equivalentAmountField: 'receivedAmount',
          errorPath: 'financialInformation.receivedAmount'
        })
      )
  });
};

export const validateCurrencyRateLogical = (
  values,
  index,
  setFieldError,
  t
) => {
  const isTradeRateValid = checkTradeRateValidation({
    values,
    setFieldError,
    tradeRate: `rates[${index}].rate`,
    isTradeRateValid: 'isTradeRateValid',
    t
  });
  return isTradeRateValid;
};
export const submitBugReportValidationSchema = (t) =>
  Yup.object({
    title: STRING_REQUIRED_YUP_OBJECT(t).max(
      50,
      t(MAXIMUM_CHARACTER_50_MESSAGE)
    )
  });

export const submitOneWayConnectionDraftSubmission = (t) =>
  Yup.object({
    branchId: STRING_REQUIRED_YUP_OBJECT(t),
    date: PAST_DATE_REQUIRED_YUP_OBJECT(t),
    draftNumber: NUMBER_REQUIRED_YUP_OBJECT(t),
    draftAmount: POSITIVE_AMOUNT_REQUIRED_YUP_OBJECT(t),
    senderFullName: STRING_REQUIRED_YUP_OBJECT(t),
    receiverFullName: STRING_REQUIRED_YUP_OBJECT(t),
    paymentPlace: STRING_REQUIRED_YUP_OBJECT(t),
    draftCurrencyCode: STRING_REQUIRED_YUP_OBJECT(t),
    otherNumber: STRING_REQUIRED_YUP_OBJECT(t)
  });

export const submitBuyChargeValidation = (t) =>
  Yup.object({
    chargeNumber: Yup.string()
      .required(t('Required field message'))
      .test('phone-number-validation', '', function buyChargeValidation(value) {
        const countryRules = {
          '09': PHONE_NUMBER_IRAN(t),
          '05': PHONE_NUMBER_TURKEY(t),
          '07': PHONE_NUMBER_AFGHAN(t),
          '03': PHONE_NUMBER_PAKISTAN(t)
        };

        // find prefix
        const prefix = Object.keys(countryRules).find((p) =>
          value.startsWith(p)
        );

        if (prefix) {
          try {
            countryRules[prefix].validateSync(value);
            return true;
          } catch (error) {
            return this.createError({ message: error.message });
          }
        }
        return this.createError({
          message: t(t('invalid number'))
        });
      }),
    currency: CURRENCY_OBJ_YUP_OBJECT(t)
  });

export const submitBuySnapChargeValidation = (t) =>
  Yup.object({
    chargeNumber: JUST_IRAN_PHONE_NUMBER(t)
  });
export const submitBuyPubgChargeValidation = (t) =>
  Yup.object({
    chargeNumber: PUBG_ID_LENGTH(t),
    currency: CURRENCY_OBJ_YUP_OBJECT(t)
  });
export const openingAustralianBankAccountValidation = (t) =>
  Yup.object({
    customerName: STRING_REQUIRED_YUP_OBJECT(t),
    homePhoneNumber: STRING_REQUIRED_YUP_OBJECT(t),
    holderName1: STRING_REQUIRED_YUP_OBJECT(t),
    holderName2: STRING_REQUIRED_YUP_OBJECT(t),
    documentId1: STRING_REQUIRED_YUP_OBJECT(t),
    documentId2: STRING_REQUIRED_YUP_OBJECT(t)
  });
export const registerNoteValidationSchema = (t) =>
  Yup.object().shape({
    description: STRING_REQUIRED_YUP_OBJECT(t),
    reminderDate: Yup.number().nullable(),
    referenceType: Yup.number().required(t(REQUIRED_MESSAGE)),
    referenceId: STRING_REQUIRED_YUP_OBJECT(t)
  });

export const registerPaymentOrderReceiptSchema = (t, remaining) =>
  Yup.object({
    exchangerManagerId: STRING_REQUIRED_YUP_OBJECT(t),
    amount: Yup.number()
      .required(t(REQUIRED_MESSAGE))
      .max(remaining, t('AmountIsGreaterThanRemaining'))
  });

export const rejectActionLogSchema = (t) =>
  Yup.object({
    rejectReason: STRING_REQUIRED_YUP_OBJECT(t)
  });
